/**
 * Locale registry: the one list of locales the app offers, the UI language each
 * one loads, and how Swiss German is derived from German at load time.
 *
 * A *locale* (BCP 47, e.g. `de-CH`) is what the user picks in Settings and what
 * every `Intl` call formats with. The *language* is the i18next language whose
 * bundle renders the text. `en-US`, `en-GB` and `en-AU` all render English but
 * format dates differently; `de-DE` and `de-CH` both render German, and `de-CH`
 * additionally gets Swiss spelling (no ß) and Swiss number formats (1'234.50).
 *
 * `i18n.ts` and the Settings picker both import from here so the two can never
 * disagree about which locales exist.
 */
/** Languages with a bundle under public/locales. `de-CH` is derived from `de`. */
export const BUNDLED_LANGUAGES = ['en', 'es', 'fr', 'de', 'de-CH'];
const DEFAULT_LOCALE = 'en-US';
/** localStorage key of the general-settings blob (timezone, timeFormat, locale, ...). */
export const GENERAL_SETTINGS_STORAGE_KEY = 'newlumen_general_settings';
/** Everything the Settings > General locale picker offers, in display order. */
export const SUPPORTED_LOCALES = [
    { value: 'en-US', language: 'en', labelKey: 'general.locale.enUS' },
    { value: 'en-GB', language: 'en', labelKey: 'general.locale.enGB' },
    { value: 'en-AU', language: 'en', labelKey: 'general.locale.enAU' },
    { value: 'de-DE', language: 'de', labelKey: 'general.locale.deDE' },
    { value: 'de-CH', language: 'de-CH', labelKey: 'general.locale.deCH' },
    { value: 'fr-FR', language: 'fr', labelKey: 'general.locale.frFR' },
    { value: 'fr-CH', language: 'fr', labelKey: 'general.locale.frCH' },
    { value: 'es-ES', language: 'es', labelKey: 'general.locale.esES' },
];
/**
 * Closest offered locale for browser tags the picker does not list verbatim.
 * Region drives formatting, so Austria maps to the German conventions and
 * Liechtenstein to the Swiss ones.
 */
const LOCALE_ALIASES = {
    'de-at': 'de-DE',
    'de-lu': 'de-DE',
    'de-li': 'de-CH',
    'fr-be': 'fr-FR',
    'fr-lu': 'fr-FR',
    'fr-ca': 'fr-FR',
    'en-nz': 'en-AU',
    'en-ie': 'en-GB',
    'en-za': 'en-GB',
    'en-in': 'en-GB',
    'en-ca': 'en-US',
    'es-mx': 'es-ES',
    'es-ar': 'es-ES',
    'es-cl': 'es-ES',
    'es-co': 'es-ES',
};
function primaryOf(tag) {
    return tag.toLowerCase().split(/[-_]/)[0] ?? '';
}
function regionOf(tag) {
    return tag.toUpperCase().split(/[-_]/)[1] ?? '';
}
function findOffered(tag) {
    const lower = tag.toLowerCase();
    return SUPPORTED_LOCALES.find((l) => l.value.toLowerCase() === lower);
}
/**
 * Which bundled UI language renders a locale. Unknown or unbundled locales
 * (`pt-BR`, `ja-JP` from older stored preferences) render English rather than
 * pointing i18next at a bundle that does not exist.
 */
export function resolveLanguage(locale) {
    if (!locale)
        return 'en';
    const offered = findOffered(locale);
    if (offered)
        return offered.language;
    const primary = primaryOf(locale);
    if (primary === 'de') {
        const region = regionOf(locale);
        return region === 'CH' || region === 'LI' ? 'de-CH' : 'de';
    }
    return primary === 'es' || primary === 'fr' ? primary : 'en';
}
/** Intl locale to use when only an i18next language is known. */
export function defaultLocaleForLanguage(language) {
    switch (language) {
        case 'de':
            return 'de-DE';
        case 'de-CH':
            return 'de-CH';
        case 'fr':
            return 'fr-FR';
        case 'es':
            return 'es-ES';
        default:
            return DEFAULT_LOCALE;
    }
}
function browserLanguages() {
    if (typeof navigator === 'undefined')
        return [];
    const list = navigator.languages && navigator.languages.length > 0 ? navigator.languages : [];
    return list.length > 0 ? list : navigator.language ? [navigator.language] : [];
}
/**
 * The offered locale closest to the browser's language preferences. Used as the
 * default for a user who has never chosen one, so a clinic in Zürich starts in
 * German with Swiss formats instead of US English.
 */
export function detectDefaultLocale(languages = browserLanguages()) {
    for (const tag of languages) {
        if (!tag)
            continue;
        const offered = findOffered(tag);
        if (offered)
            return offered.value;
        const alias = LOCALE_ALIASES[tag.toLowerCase()];
        if (alias)
            return alias;
    }
    // Second pass: match on the language alone ("de", "fr-XX", …).
    for (const tag of languages) {
        if (!tag)
            continue;
        const primary = primaryOf(tag);
        const first = SUPPORTED_LOCALES.find((l) => primaryOf(l.value) === primary);
        if (first)
            return first.value;
    }
    return DEFAULT_LOCALE;
}
/** The `locale` field of a serialised general-settings blob, if any. */
export function localeFromSettings(raw) {
    if (!raw)
        return null;
    try {
        const settings = JSON.parse(raw);
        return typeof settings.locale === 'string' && settings.locale ? settings.locale : null;
    }
    catch {
        return null;
    }
}
/** The locale the user chose in Settings, else the browser's closest offered one. */
export function getPreferredLocale() {
    try {
        const stored = localeFromSettings(localStorage.getItem(GENERAL_SETTINGS_STORAGE_KEY));
        if (stored)
            return stored;
    }
    catch {
        /* no storage (privacy mode, SSR) */
    }
    return detectDefaultLocale();
}
/** Languages the help site (frontend/docs-ui) ships a translated tree for. */
const HELP_SITE_LANGUAGES = ['de'];
/**
 * Same-origin path of the user guide for a UI language: `/docs/de/` for German
 * (de and de-CH share one German tree), `/docs/` for everything else.
 */
export function helpSiteUrlFor(language) {
    const primary = primaryOf(language ?? '');
    return HELP_SITE_LANGUAGES.includes(primary)
        ? `/docs/${primary}/`
        : '/docs/';
}
/**
 * Whether a locale's users expect a 12- or 24-hour clock, taken from the
 * runtime's own locale data so the answer matches what `Intl` would print.
 */
export function defaultTimeFormat(locale) {
    try {
        const { hour12 } = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions();
        return hour12 === false ? '24h' : '12h';
    }
    catch {
        return '12h';
    }
}
/** The bundle folder under public/locales that serves an i18next language. */
export function bundleLanguageFor(language) {
    return language === 'de-CH' ? 'de' : language;
}
/** Swiss Standard German has no ß: "Straße" is written "Strasse". */
export function swissifyGerman(value) {
    return value.replace(/ß/g, 'ss').replace(/ẞ/g, 'SS');
}
/**
 * Helvetisms applied on top of the ß rule, as stems so inflections are covered:
 * "Überweisende Praxis" → "Zuweisende Praxis", "Überweiser" → "Zuweiser".
 * (In Switzerland a referral is a Zuweisung; app-ui has no bank-transfer copy,
 * so mapping the stem is safe.)
 */
const DE_CH_TERMS = [
    [/Überweis/g, 'Zuweis'],
    [/überweis/g, 'zuweis'],
];
/**
 * Per-key overrides for strings where a stem rule is not enough. Keyed by
 * namespace then translation key; values replace the derived German entirely.
 *
 * Currently empty: the German bundles needed only the eszett rule and the
 * Ueberweis/Zuweis stem above. Add an entry here when a Helvetism is specific
 * to one string rather than a stem (Bundesland vs Kanton, for instance).
 */
const DE_CH_KEY_OVERRIDES = {};
/** A single German string in Swiss Standard German. */
export function toSwissGerman(value) {
    let out = swissifyGerman(value);
    for (const [pattern, replacement] of DE_CH_TERMS)
        out = out.replace(pattern, replacement);
    return out;
}
/**
 * Derive the `de-CH` bundle for a namespace from the `de` bundle. Runs at load
 * time in `i18n.ts`, so Swiss German never needs its own JSON files and can
 * never drift from German.
 */
export function deriveSwissGerman(namespace, bundle) {
    const overrides = DE_CH_KEY_OVERRIDES[namespace] ?? {};
    const out = {};
    for (const [key, value] of Object.entries(bundle)) {
        if (key in overrides)
            out[key] = overrides[key];
        else
            out[key] = typeof value === 'string' ? toSwissGerman(value) : value;
    }
    return out;
}
